import type { AgentMessage, AgentTask, TaskPayload } from '../types';
import { adsChangeMadeByHand } from './adsChangeStatus';

/**
 * The conversation view: the default way a task opens on the Tasks page, for every agent. A practice
 * switched to the classic view (flag `classic-agent-tasks`, Command Center → Agents) keeps
 * TaskWorkspace's tabs instead.
 *
 * Each agent turn is framed by a fixed lead line above its draft or report, a closing question, and
 * suggested replies. The lead and closing question are static copy: a skill listed in SCRIPTS gets
 * its own, any other skill gets a default for its kind (a report or a proposal). The suggested replies
 * follow the conversation: once the agent has answered, they are the follow-ups it wrote with that
 * answer (`threadSuggestions`); the script's list is only the opening set, and the fallback for an
 * answer that came with none. A suggestion is sent as an ordinary thread message, so the agent's
 * answer is real.
 */
export interface ConversationScript {
  /** The agent's opening line, above the draft or report card. */
  lead: string;
  /** The question the agent closes its first turn with, while the task is still open. */
  prompt: string;
  /** Suggested replies, each sent as-is to the thread. */
  suggestions: readonly string[];
}

const SCRIPTS: Record<string, ConversationScript> = {
  // Joe (community)
  community_scout: {
    lead: 'Here are this month’s picks: local businesses whose customers overlap with your patients.',
    prompt: 'Want me to plan a first touch with any of them?',
    suggestions: [
      'Why these picks?',
      'Plan a first touch with the top pick',
      'Find picks closer to the office',
    ],
  },
  touch_draft: {
    lead: 'I wrote the draft for this touch. You send it; nothing goes out from here.',
    prompt: 'Want it shorter, warmer or more specific?',
    suggestions: ['Make it shorter', 'Make it warmer', 'Make it more specific to them'],
  },
  cadence_keeper: {
    lead: 'It’s time to reconnect with this partner. Here’s the touch I’d suggest.',
    prompt: 'Does this touch fit, or would you rather do something else?',
    suggestions: ['Suggest a different touch', 'Space these out more', 'Why this touch now?'],
  },
  referral_checkin: {
    lead: 'Here’s this month’s one-minute referral check-in, prefilled from what I could see.',
    prompt: 'Did I get anything wrong for a partner?',
    suggestions: ['Who hasn’t referred lately?', 'Which partners sent the most?'],
  },
  // Sam (seo)
  gbp_post: {
    lead: 'I drafted a Google Business Profile post for you.',
    prompt: 'Want me to change anything before you approve it?',
    suggestions: ['Make it shorter', 'Try a different angle', 'Why this topic?'],
  },
  gbp_listing_edit: {
    lead: 'I’d like to change something on your Google Business Profile.',
    prompt: 'Want me to adjust it before you decide?',
    suggestions: ['Why does this matter?', 'What will patients see?', 'Make it simpler'],
  },
  // Jenny (ads)
  ad_reporting: {
    lead: 'Here’s your ads read: what ran, what it cost and what came of it.',
    prompt: 'Want me to dig into anything here?',
    // Questions the report itself answers. "What should I change?" is left to the monthly strategy
    // review: this report states facts and never prescribes.
    suggestions: [
      'What stood out?',
      'Compare with last month',
      'Which campaigns brought calls?',
      'What were people searching?',
      'What changed this month?',
      'How does Google count conversions?',
    ],
  },
  ad_campaign_change: {
    lead: 'I’d like to change one of your campaigns. Approving applies it in Google Ads, after checking nothing changed there since.',
    prompt: 'Want me to adjust it before you decide?',
    suggestions: ['What do you expect this to change?', 'Make it a smaller change', 'Why now?'],
  },
  // Betty (reviews)
  review_funnel_report: {
    lead: 'Here’s how your review requests are doing.',
    prompt: 'Want me to dig into anything here?',
    suggestions: ['What should I change first?', 'Why are reviews slowing down?'],
  },
  send_timing_tune: {
    lead: 'I’d like to change when your review texts go out.',
    prompt: 'Want me to adjust it before you decide?',
    suggestions: ['Why this timing?', 'What happens if I approve?'],
  },
  invite_copy_tune: {
    lead: 'I’d like to try new wording for your review request text.',
    prompt: 'Want me to adjust the wording before you decide?',
    suggestions: ['Make it shorter', 'Make it warmer', 'What happens if I approve?'],
  },
  private_feedback_recovery: {
    lead: 'A patient left private feedback. Here’s how I’d suggest following up.',
    prompt: 'Want me to change the approach?',
    suggestions: ['Make it more personal', 'Should we call instead?'],
  },
};

const REPORT_DEFAULT: ConversationScript = {
  lead: 'Here’s what I found.',
  prompt: 'Want me to dig into anything here?',
  suggestions: ['What should I do first?', 'Explain this in plain words'],
};

const PROPOSAL_DEFAULT: ConversationScript = {
  lead: 'Here’s what I’d like to do. Nothing changes until you approve.',
  prompt: 'Want me to change anything before you decide?',
  suggestions: ['Why this?', 'What happens if I approve?', 'Make it simpler'],
};

/**
 * A change drafted before Jenny applied changes herself says "make the change in Google Ads yourself":
 * the lead must not promise that approving applies it (Little Heroes, Oct 21 draft, 2026-11-03).
 */
const ADS_CHANGE_BY_HAND: ConversationScript = {
  lead: 'I’d like to change one of your campaigns. This is an older draft: approving records your decision, and you make the change in Google Ads yourself.',
  prompt: 'Want me to adjust it before you decide?',
  suggestions: ['What do you expect this to change?', 'Make it a smaller change', 'Why now?'],
};

/** The conversation framing for a task: its skill's own script, or the default for its kind. */
export function conversationScript(
  task: Pick<AgentTask, 'skillKey' | 'bucket'> & { payload?: Pick<TaskPayload, 'sections'> },
): ConversationScript {
  if (task.skillKey === 'ad_campaign_change') {
    const change = task.payload?.sections?.find((section) => section.key === 'change')?.value;
    if (adsChangeMadeByHand(typeof change === 'string' ? change : undefined))
      return ADS_CHANGE_BY_HAND;
  }
  return SCRIPTS[task.skillKey] ?? (task.bucket === 'audit' ? REPORT_DEFAULT : PROPOSAL_DEFAULT);
}

const CLOSED_STATES: ReadonlySet<AgentTask['state']> = new Set(['done', 'cancelled', 'failed']);

/** Work the practice has finished with: the agent stops asking and suggesting. */
export function isClosedTask(task: Pick<AgentTask, 'state'>): boolean {
  return CLOSED_STATES.has(task.state);
}

/** Suggestions not yet sent: one the practice already sent (same text) is not offered again. */
export function remainingSuggestions(
  script: ConversationScript,
  sentBodies: readonly string[],
): string[] {
  const sent = new Set(sentBodies.map((b) => b.trim().toLowerCase()));
  return script.suggestions.filter((s) => !sent.has(s.toLowerCase()));
}

/**
 * The replies to offer now. After the agent's latest answer, the follow-ups it wrote with it, about what it
 * just said; before any answer, or after one that came with none, the script's opening set. Never one the
 * practice already sent.
 */
export function threadSuggestions(
  script: ConversationScript,
  thread: ReadonlyArray<Pick<AgentMessage, 'direction' | 'body' | 'followUps'>>,
): string[] {
  const sent = new Set(
    thread.filter((m) => m.direction === 'inbound').map((m) => m.body.trim().toLowerCase()),
  );
  const latest = thread[thread.length - 1];
  const written = latest?.direction === 'outbound' ? (latest.followUps ?? []) : [];
  const fresh = written.filter((s) => !sent.has(s.trim().toLowerCase()));
  return fresh.length > 0 ? fresh : remainingSuggestions(script, [...sent]);
}

/**
 * What the conversation footer shows. Before the practice has written anything, a waiting decision has the
 * footer to itself (the first look is the review), and "Ask <agent>" / "Make changes" swaps in the message box.
 * Once the practice is talking with the agent, the message box stays open under the decision: answering a
 * question never closes the conversation, and the decision ("Mark reviewed", approve) stays one click away.
 */
export function footerMode(state: { deciding: boolean; changing: boolean; ownerSpoke: boolean }): {
  showDecision: boolean;
  showPrompt: boolean;
  /** The "What should <agent> change?" heading with its "Back to decision" link. */
  showChangesHead: boolean;
} {
  const chatting = state.ownerSpoke;
  return {
    showDecision: !state.changing || chatting,
    showPrompt: !state.deciding || state.changing || chatting,
    showChangesHead: state.changing && !chatting,
  };
}

/**
 * How a thread message shows in the conversation:
 * - `chat`: the practice's messages, the agent's replies, and system notices.
 * - `activity`: something the agent recorded or sent about this task (a working note, an approval
 *   request or draft notification it emailed). One folded line; the text opens on request.
 * - `hidden`: a digest anchored on this task but about all of the agent's work (the progress report,
 *   the day-one report). It is not part of this conversation.
 */
export type ThreadEntryRole = 'chat' | 'activity' | 'hidden';

const ACTIVITY_LABEL: Record<string, string> = {
  checkpoint: 'Working note',
  approval: 'Approval request sent',
  draft: 'Draft notification sent',
  block: 'Blocked',
  stopped: 'Stopped',
  // Only an agency viewer receives these (the backend leaves them out for the practice).
  agency_note: 'Note for the agency',
  access_request: 'Access request sent',
};

const HIDDEN_KINDS: ReadonlySet<string> = new Set(['progress_report', 'day1_report']);

export function threadEntryRole(
  m: Pick<AgentMessage, 'direction' | 'kind' | 'producedVersion'>,
): ThreadEntryRole {
  // A message that made a new version is the agent answering, whatever it is filed as.
  if (m.direction === 'inbound' || !m.kind || m.producedVersion != null) return 'chat';
  if (HIDDEN_KINDS.has(m.kind)) return 'hidden';
  if (ACTIVITY_LABEL[m.kind]) return 'activity';
  return 'chat';
}

/** The folded line's label for an activity entry. */
export function activityLabel(kind: string | undefined): string {
  return (kind && ACTIVITY_LABEL[kind]) || 'Update';
}

/**
 * One line for the pinned decision bar saying what the decision is about, so it can be read without
 * scrolling back to the card: the type of work plus its headline (the first short piece of content
 * in the draft), and how many things the draft asks the practice to confirm before approving.
 */
export function decisionContext(
  task: Pick<AgentTask, 'payload'>,
  typeLabel: string,
): { subject: string; checks: number } {
  const sections = task.payload?.sections ?? [];
  const headline = sections
    .filter((s) => s.role === 'content' && typeof s.value === 'string')
    .map((s) => (s.value as string).trim())
    .find((v) => v.length > 0 && v.length <= 90 && !v.includes('\n'));
  const checks = sections
    .filter((s) => s.role === 'check')
    .reduce((n, s) => n + (Array.isArray(s.value) ? s.value.length : s.value ? 1 : 0), 0);
  return { subject: headline ? `${typeLabel}: “${headline}”` : typeLabel, checks };
}

/**
 * An agent's message as the chat shows it. Older messages were stored with the email sign-off
 * ("— Jenny, your ads specialist") and some open with "Jenny here."; the chat already says who wrote
 * each message, so both are dropped. The text itself is not reworded.
 */
export function agentMessageText(body: string, agentName: string): string {
  const name = agentName.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (!name) return body.trim();
  const signOff = new RegExp(`\\n\\s*\\n\\s*(?:[—–-]\\s*)?${name},[^\\n]*\\s*$`, 'i');
  const greeting = new RegExp(`^\\s*${name} here[.!,:]?\\s*`, 'i');
  return separateLists(body.replace(signOff, '').replace(greeting, '').trim());
}

const LIST_ITEM = /^\s*(?:[-*+]|\d+[.)])\s+/;
const TABLE_ROW = /^\s*\|/;

/**
 * A blank line after a list or table when prose follows it straight away. Markdown otherwise folds the next line
 * into the last item ("Lost to budget: 32.7% On the overall figure, ad rank is ..."), and the answer reads as one
 * blob.
 */
export function separateLists(text: string): string {
  const lines = text.split('\n');
  const out: string[] = [];
  lines.forEach((line, i) => {
    const prev = lines[i - 1];
    const block = (l: string | undefined) =>
      l !== undefined && (LIST_ITEM.test(l) || TABLE_ROW.test(l));
    if (prev !== undefined && block(prev) && line.trim() && !block(line) && !/^\s{2,}/.test(line))
      out.push('');
    out.push(line);
  });
  return out.join('\n');
}

/** The draft version an agent's message names ("saved as version 14"), when that version exists. */
export function mentionedVersion(body: string, versionCount: number): number | undefined {
  const match = /\bversion (\d+)\b/i.exec(body) ?? /\bv(\d+)\b/.exec(body);
  const n = match ? Number(match[1]) : NaN;
  return Number.isInteger(n) && n >= 1 && n <= versionCount ? n : undefined;
}

/**
 * The question an answer quotes above itself: the practice message it answers, when that is not the message just
 * above it (several questions sent together get their answers one at a time, below all of them).
 */
export function replyQuoteText(
  message: { direction: string; inReplyTo?: string },
  previous: { id: string } | undefined,
  messages: ReadonlyArray<{ id: string; body: string }>,
): string | undefined {
  if (
    message.direction !== 'outbound' ||
    !message.inReplyTo ||
    previous?.id === message.inReplyTo
  ) {
    return undefined;
  }
  const quoted = messages.find((m) => m.id === message.inReplyTo);
  if (!quoted) return undefined;
  const text = quoted.body.trim().replace(/\s+/g, ' ');
  return text.length > 160 ? `${text.slice(0, 160)}…` : text;
}
